/**
 * Infer a slide's *layout* from its slug.
 *
 * Slides don't carry an explicit layout field — the renderer reads
 * ``bodyHtml`` verbatim — so we treat the slug as the layout hint.
 * The match is intentionally generous (substring lookup) so editors
 * keep working when someone makes a deck whose slug is, say,
 * ``intro-opener`` instead of the canonical ``opener-thread-thesis``.
 *
 * Anything we don't recognise falls back to ``generic`` — the JSON
 * textarea + standard meta inputs.
 */

import type { Slide } from "../types";

export type LayoutKind = "opener" | "two-col" | "paths" | "asks" | "generic";

const PATTERNS: Array<{ kind: LayoutKind; needle: RegExp }> = [
  { kind: "opener", needle: /(^|[-_])opener(?=$|[-_])/i },
  { kind: "opener", needle: /thesis/i },
  { kind: "two-col", needle: /two[-_]?col/i },
  { kind: "two-col", needle: /(data-black-market|now-with-thread)/i },
  { kind: "paths", needle: /paths?/i },
  { kind: "paths", needle: /(selection-as-action|provenance-dag-branches)/i },
  { kind: "asks", needle: /asks?/i },
  { kind: "asks", needle: /(what-thread-unlocks|not-solving)/i },
];

export function detectLayout(slide: Slide): LayoutKind {
  const slug = (slide.slug ?? "").toLowerCase();
  for (const { kind, needle } of PATTERNS) {
    if (needle.test(slug)) return kind;
  }
  return "generic";
}

export function layoutLabel(kind: LayoutKind): string {
  switch (kind) {
    case "opener":
      return "Opener";
    case "two-col":
      return "Two-column";
    case "paths":
      return "Paths";
    case "asks":
      return "Asks";
    case "generic":
      return "Generic";
  }
}
